import { select, Store } from '@ngrx/store';
import { Injectable } from '@angular/core';
import { take, map, filter } from 'rxjs/operators';
import { Observable, zip } from 'rxjs';

import { State } from '@app/store/state/state';
import { HotelSearched } from '@hotels/hotel-results/entities/hotel-searched';
import { HotelFilterApplier } from '@shared/hotel-itinerary/hotel-filters';
import { DeeplinkFilterParams, HotelSearchPayload } from '@hotels/hotel-results/entities/hotel-search.payload';
import { HotelSearchResultsData } from '@hotels/hotel-results/entities/hotel-search-results-data';
import { hotelFiltersPayload } from '@hotels/hotel-results/store/selectors/hotel-filters-payload.selector';
import { HotelFiltersPayloadState } from '@hotels/hotel-results/store/reducers/hotel-filters-payload.reducer';
import {
  isLoadingHotelFilters, getUIHotelsState, isLoadingHotelMarkers
} from '@hotels/hotel-results/store/selectors/ui.selector';
import {
  fetchHotelsWithFiltersAction,
  searchHotelsAction,
  clearHotelResultsAction,
  fetchHotelsWithPaginationAction,
  searchHotelsMarkersAction,
  setFilterSorterAction,
  searchLocationHotelAction,
  disableHotelResultScrollAction,
} from '@hotels/hotel-results/store/actions/hotels.actions';
import {
  hotelResults, hotelSearchData, hotelResultsWithMarkers, searchedHotel, hotelResultsExclusiveOffer
} from '@hotels/hotel-results/store/selectors/hotel-results.selector';
import { HotelMarker } from '@hotels/hotel-results/entities/hotel-marker';
import { HotelResultsUiModuleState } from '@hotels/hotel-results/store/state/hotel-results-ui-module.state';
import { FilterSorterCriterias } from '@shared/filters/entities/fetch-filters.response';
import { PriceWidget, AmenitiesWidget } from '@app/entities/flight-results-widget';
import { ZoneResponse } from '@app/entities/zones.response';
import { hotelsTopZones } from '@hotels/hotel-results/store/selectors/hotel-top-zones.selector';
import { hotelResultsRootSelector } from '@hotels/hotel-results/store/selectors/hotel-results-root.selector';
import { HotelSearchLocation } from '@hotels/hotel-results/entities/hotel-search-location';
import { ErrorHandle } from '@shared/no-results/entities/results-errors';
import { GlobalFacade } from '@app/global.facade';
import { HotelFiltersResponse, HotelFilterVm } from '@shared/magic-filter-bar/entities/filter-types';
import { amenitiesForWidget, hotelsInfoPrice } from '@app/store/selectors/global-hotel-filters.selector';
import {
  DEFAULT_HOTEL_FILTERS, DEFAULT_HOTEL_PAGINATION, mapHotelFilterPayload
} from '@app/mappers/global-filters/hotel-global-filters.mapper';
import { HotelFilterSearchPayload } from '@hotels/hotel-results/entities/hotel-filter-search.payload';

@Injectable()
export class HotelResultsFacade {

  constructor(
    private readonly store: Store<State>,
    private readonly globalFacade: GlobalFacade,
  ) { }

  public hotelsWithMarkers$: Observable<HotelMarker[]> =
    this.store.pipe(select(hotelResultsWithMarkers));
  public hotelResults$: Observable<HotelSearched[]> = this.store.pipe(select(hotelResults));
  public hotelResultsExclusiveOffer$: Observable<HotelSearched[]> = this.store.pipe(select(hotelResultsExclusiveOffer));
  public searchedHotel$: Observable<HotelSearched> = this.store.pipe(select(searchedHotel));
  public hotelFilters$: Observable<HotelFilterVm[]> = this.globalFacade.hotelFilterVm$();
  public hotelFiltersResponse$: Observable<HotelFiltersResponse> = this.globalFacade.hotelFilters$;
  public hotelSearchData$: Observable<HotelSearchResultsData> = this.store.pipe(select(hotelSearchData));
  public hotelSearchResultCount$: Observable<number> = this.store.pipe(select(hotelResultsRootSelector),
    map(x => x.hotelResults.ResultsCount));
  public hotelSearchResultTotalCount$: Observable<number> = this.store.pipe(select(hotelResultsRootSelector),
    map(x => x.hotelResults.ResultsTotalCount));
  public hotelTopZones$: Observable<ZoneResponse[]> = zip(
    this.store.pipe(select(hotelsTopZones)),
    this.store.pipe(select(hotelFiltersPayload))
  ).pipe(
    map(([zoneList, payload]) => zoneList.filter(z => payload.Zones.indexOf(z.Name) === -1))
  );
  public infoPriceWidgets$: Observable<PriceWidget[]> = this.store.pipe(select(hotelsInfoPrice));
  public infoAmenitiesWidgets$: Observable<AmenitiesWidget[]> = this.store.pipe(select(amenitiesForWidget));
  public hotelFiltersPayload$: Observable<HotelFiltersPayloadState> = this.store.pipe(select(hotelFiltersPayload));
  public isLoadingHotelFilters$: Observable<boolean> = this.store.pipe(select(isLoadingHotelFilters));
  public isLoadingHotelResults$: Observable<boolean> = this.globalFacade.ui.results.isLoadingResults$;
  public isLoadingHotelMarkers$: Observable<boolean> = this.store.pipe(select(isLoadingHotelMarkers));
  public disabledHotelResultsScroll$: Observable<boolean> = this.store.pipe(
    select(getUIHotelsState), map(state => state.disabledHotelResultsScroll)
  );

  public errors$: Observable<ErrorHandle> = this.globalFacade.ui.errorHandle$;

  public errorInHotels$: Observable<boolean> = this.store
    .pipe(select(getUIHotelsState), map((state: HotelResultsUiModuleState) => state.errorInHotels.status));

  public sortingHasBeenChanged$: Observable<boolean> = this.globalFacade.ui.results.filters.sortingHasBeenChanged$;

  public hotelSortingHasBeenChanged(payload: boolean): void {
    this.globalFacade.ui.results.filters.sortingHasBeenChanged(payload);
  }

  public searchHotels(
    payload: HotelSearchPayload,
    filters: DeeplinkFilterParams = { Zones: [], Categories: [] },
    isByhours: boolean,
    hourType: string[]
  ): void {
    this.store.dispatch(searchHotelsAction({ payload, filters, isByhours, hourType }));
  }

  public searchHotelsMarkers(payload: HotelFiltersPayloadState): void {
    this.store.dispatch(searchHotelsMarkersAction({ payload }));
  }

  public searchLocationHotel(payload: HotelSearchLocation, isByhours: boolean): void {
    this.clearHotelResults();
    this.store.dispatch(searchLocationHotelAction({ payload, isByhours }));
  }

  public fetchHotelsMarkersWithFilters(filters?: HotelFilterApplier): void {
    this.clearHotelResults();
    this.hotelFiltersPayload$
      .pipe(take(1))
      .subscribe(selectedFilters =>
        this.store.dispatch(fetchHotelsWithFiltersAction({
          payload: {
            ...(filters ? mapHotelFilterPayload(filters, selectedFilters) : selectedFilters),
            CombineRooms: false,
            ResultCountLowerBound: 0,
            ResultCountUpperBound: selectedFilters.ResultsCount
          } as HotelFilterSearchPayload
        })))
      .unsubscribe();
  }

  public resetHotelFilters(isMapView: boolean): void {
    this.clearHotelResults();
    this.hotelFiltersPayload$
      .pipe(take(1))
      .subscribe(_payload =>
        this.store.dispatch(fetchHotelsWithFiltersAction({
          payload: {
            ..._payload,
            ...DEFAULT_HOTEL_PAGINATION,
            ...DEFAULT_HOTEL_FILTERS,
            ResultCountUpperBound: isMapView ? _payload.ResultsCount : DEFAULT_HOTEL_PAGINATION.ResultCountUpperBound
          }
        })))
      .unsubscribe();
  }

  public fetchHotelsWithFilters(filters?: HotelFilterApplier): void {
    this.clearHotelResults();
    this.hotelFiltersPayload$
      .pipe(take(1))
      .subscribe(selectedFilters =>
        this.store.dispatch(fetchHotelsWithFiltersAction({
          payload: {
            ...(filters ? mapHotelFilterPayload(filters, selectedFilters) : selectedFilters)
          } as HotelFilterSearchPayload
        })))
      .unsubscribe();
  }

  public setFilterSorter(payload: FilterSorterCriterias): void {
    this.store.dispatch(setFilterSorterAction({ payload }));
  }

  public searchHotelsWithPagination(): void {
    zip(
      this.hotelFiltersPayload$,
      this.hotelSearchData$
    ).pipe(
      take(1),
      map(data => {
        return {
          searchPayload: data[0],
          searchResultsData: data[1]
        };
      }),
      filter(data => data.searchPayload.ResultsCount > data.searchPayload.ResultCountUpperBound)
    ).subscribe(data => {
      const payload = {
        payload: {
          ...data.searchPayload,
          ResultCountLowerBound: data.searchPayload.ResultCountUpperBound + 1,
          ResultCountUpperBound: data.searchPayload.ResultCountUpperBound + 10,
        }
      };
      this.store.dispatch(fetchHotelsWithPaginationAction(payload));
    }).unsubscribe();
  }

  public clearHotelResults(): void {
    this.store.dispatch(clearHotelResultsAction());
  }

  public disabledResultsScroll(payload: boolean): void {
    this.store.dispatch(disableHotelResultScrollAction({ payload }));
  }
}
